import React, { useState, useEffect, memo } from 'react';
import config from '../config';
import ExportDataButton from './ExportDataButton';

const CouponInput = memo(({ 
  value, 
  onChange, 
  error 
}: { 
  value: string; 
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void; 
  error: string; 
}) => (
  <div className="mt-4">
    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
      Sudah memiliki kode kupon? Masukkan kode kupon di sini atau biarkan kosong jika tidak memiliki kode kupon
    </p>
    <div className="flex items-center space-x-2">
      <input
        type="text"
        value={value}
        onChange={onChange}
        placeholder="Enter coupon code"
        className="w-64 px-3 py-2 bg-white dark:bg-[#40414F] border rounded-md text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
      />
    </div>
    {error && (
      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
        {error}
      </p>
    )}
  </div>
));

const Subscription = () => {
  useEffect(() => {
    document.body.style.height = '100%';
    document.documentElement.style.height = '100%';
    return () => {
      document.body.style.height = '';
      document.documentElement.style.height = '';
    };
  }, []);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingPro, setIsLoadingPro] = useState(false);
  const [couponCodePro, setCouponCodePro] = useState('');
  const [couponErrorPro, setCouponErrorPro] = useState('');
  const [currentPlan, setCurrentPlan] = useState<string | null>(null);
  const [userStatus, setUserStatus] = useState<string | null>(null);
  // null while unknown. When false we hide every payment affordance. Checkout
  // is server-created, but failing closed still keeps the UI honest.
  const [paymentsOpen, setPaymentsOpen] = useState<boolean | null>(null);
  const [waitlistUrl, setWaitlistUrl] = useState('https://powerai.id/waitlist');
  // Team members do not manage quota here; /auth/me tells us to point them at
  // the Tim page instead. Nothing else on this page changes for them.
  const [hasTeam, setHasTeam] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${config.apiUrl}/auth/intake-status`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        // A 404 (backend not yet deployed) or any error body: fail closed, so
        // the page shows the freeze notice instead of hanging on "Memuat…".
        if (!data) {
          setPaymentsOpen(false);
          return;
        }
        setPaymentsOpen(data.payments_open);
        if (data.waitlist_url) setWaitlistUrl(data.waitlist_url);
      })
      .catch(() => {
        // Fail closed: never offer checkout unless the backend confirms that
        // payments are open.
        if (!cancelled) setPaymentsOpen(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          console.error('No authentication token found');
          window.location.href = '/login';
          return;
        }

        const response = await fetch(`${config.apiUrl}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user info');
        }

        const userInfo = await response.json();
        console.log('User info from API:', userInfo);
        
        setUserStatus(userInfo.status);
        setHasTeam(!!userInfo.has_team);

        if (userInfo.status === 'lite') {
          console.log('Setting current plan to lite');
          setCurrentPlan('lite');
        } else if (userInfo.status === 'active') {
          console.log('Setting current plan to pro');
          setCurrentPlan('pro');
        } else if (userInfo.status === 'limited') {
          console.log('Setting current plan to null for limited user');
          setCurrentPlan(null);
        } else if (userInfo.status === 'expired') {
          console.log('Setting current plan to null for expired user');
          setCurrentPlan(null);
        } else {
          console.log('No current plan detected');
          setCurrentPlan(null);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };

    fetchUserInfo();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    window.location.href = '/';
  };

  const handlePayment = async () => {
    setIsLoadingPro(true);
    const couponCode = couponCodePro;
    setCouponErrorPro('');
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.error('No authentication token found');
        window.location.href = '/login';
        return;
      }

      const authHeader = `Bearer ${token}`;

      const payload = {
        plan_id: 'pro',
        ...(couponCode && { coupon_code: couponCode })
      };

      const response = await fetch(`${config.apiUrl}/subscription/select-plan`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error response:', errorData);
        
        if (errorData.detail?.errors?.coupon) {
          setCouponErrorPro(errorData.detail.errors.coupon);
          setIsLoadingPro(false);
          return;
        }

        if (errorData.detail?.code === 'payments_closed') {
          setCouponErrorPro('Pembelian sedang ditutup.');
          setIsLoadingPro(false);
          return;
        }

        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Response data:', data);
      
      if (data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        console.error('No payment URL received');
      }
    } catch (error) {
      console.error('Error fetching payment URL:', error);
      if (error instanceof Error && error.message.includes('401')) {
        window.location.href = '/login';
      }
    } finally {
      setIsLoadingPro(false);
    }
  };

  return (
    <div className="fixed inset-0 overflow-y-auto bg-white dark:bg-[#171717]">
      <div className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Logo with dark/light mode support */}
          <div className="flex justify-between items-center mb-8">
            <div className="flex-1"></div>
            <div className="flex items-center">
              <img 
                src="/logo-only200-removebg.png"
                alt="PowerAI Logo"
                className="h-16 w-auto block dark:hidden" // Show in light mode
              />
              <img 
                src="/logo-only200-removebg-inverted.png"
                alt="PowerAI Logo"
                className="h-16 w-auto hidden dark:block" // Show in dark mode
              />
            </div>
            <div className="flex-1 flex justify-end">
              <button
                onClick={() => {
                  localStorage.removeItem('accessToken');
                  window.location.href = '/login';
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Logout
              </button>
            </div>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl">
              {paymentsOpen === null
                ? 'Memuat…'
                : paymentsOpen
                ? 'Silakan lakukan pembayaran'
                : 'Pembelian sedang ditutup sementara'}
            </h2>
            <p className="mt-3 text-xl text-gray-500 dark:text-gray-400">
              {paymentsOpen === null
                ? ' '
                : paymentsOpen
                ? 'Harga langganan 1 bulan'
                : 'Kami sedang membenahi Power AI'}
            </p>
          </div>

          {hasTeam && (
            <div className="bg-gray-50 dark:bg-[#202123] rounded-2xl shadow-lg p-6 mb-8">
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Kamu tergabung di tim. Kuota tim dikelola di halaman Tim.
              </p>
              <a
                href="/team"
                className="mt-3 inline-block text-blue-600 dark:text-blue-400 hover:underline"
              >
                Buka halaman Tim
              </a>
            </div>
          )}

          {paymentsOpen === false && (
            <div className="bg-gray-50 dark:bg-[#202123] rounded-2xl shadow-lg p-8 mb-8">
              <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                Pembelian dan perpanjangan paket sedang kami tutup sementara. Kalau
                langgananmu masih berjalan, akunmu tetap bisa dipakai seperti biasa
                sampai masa berlakunya habis.
              </p>
              <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                Datamu tetap milikmu. Unduh seluruh percakapan dan lampiranmu kapan saja
                lewat tombol di bawah ini.
              </p>

              {/* Free access codes still work while paid checkout is closed. */}
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Punya kode akses gratis?
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="text"
                    value={couponCodePro}
                    onChange={(e) => {
                      setCouponCodePro(e.target.value);
                      setCouponErrorPro('');
                    }}
                    placeholder="Masukkan kode"
                    className="flex-1 px-3 py-2 bg-white dark:bg-[#40414F] border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-white"
                  />
                  <button
                    onClick={handlePayment}
                    disabled={!couponCodePro || isLoadingPro}
                    className={`px-6 py-2 rounded-md font-medium text-white transition-colors ${
                      !couponCodePro || isLoadingPro
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {isLoadingPro ? 'Memproses...' : 'Aktifkan'}
                  </button>
                </div>
                {couponErrorPro && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">{couponErrorPro}</p>
                )}
              </div>

              <ExportDataButton variant="standalone" />

              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
                <a href={waitlistUrl} className="text-blue-600 dark:text-blue-400 hover:underline">
                  Kabari aku saat Power AI dibuka lagi
                </a>
                <span className="mx-2">·</span>
                <a
                  href="https://api.whatsapp.com/send/?phone=6285601841025"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Hubungi kami via WhatsApp
                </a>
              </div>
            </div>
          )}

          {/* Expired User Warning. Suppressed during the freeze: it tells people
              to renew, which is exactly what they cannot do right now. */}
          {userStatus === 'expired' && paymentsOpen === true && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 mb-8">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-400">
                    Langganan Telah Berakhir
                  </h3>
                  <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                    Langganan Anda telah berakhir. Silakan perpanjang langganan untuk melanjutkan menggunakan PowerAI dengan semua fitur premium.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Plans are hidden entirely while payments are frozen. */}
          {paymentsOpen === true && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Pro Plan */}
            <div 
              className={`bg-gray-50 dark:bg-[#202123] rounded-2xl shadow-lg p-8 flex flex-col items-center transition duration-200 hover:shadow-2xl hover:bg-blue-50 dark:hover:bg-[#232a3b] max-w-md w-full mx-auto md:col-span-2 ${
                currentPlan === 'pro' 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'cursor-pointer hover:bg-blue-50 dark:hover:bg-[#232a3b]'
              }`}
            >
              <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">Individual</div>
              <div className="text-4xl font-bold text-blue-600 dark:text-blue-400 mb-4">Rp199.000<span className="text-xl font-normal">/bulan</span></div>
              {currentPlan === 'lite' && (
                <div className="text-sm text-green-600 dark:text-green-400 mb-4">
                  (Anda hanya perlu tambah Rp172.000 untuk upgrade ke Pro)
                </div>
              )}
              <ul className="mb-6 text-gray-700 dark:text-gray-300 text-left w-full">
                <li className="mb-2">• Satu chat pintar: riset web bersumber, baca file, bikin gambar dan presentasi</li>
                <li className="mb-2">• 10.000 kredit tiap bulan (reset setiap bulan)</li>
                <li className="mb-2">• Kredit yang tidak terpakai akan hangus di akhir bulan</li>
              </ul>
              {currentPlan === 'lite' ? (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePayment();
                    }}
                    disabled={isLoadingPro}
                    className="w-full font-bold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-2 bg-[#10A37F] hover:bg-[#0D8C6D] text-white"
                  >
                    {isLoadingPro ? 'Memproses...' : 'Bayar dengan Xendit / QRIS'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (currentPlan !== 'pro') {
                        handlePayment();
                      }
                    }}
                    disabled={isLoadingPro || currentPlan === 'pro'}
                    className={`w-full font-bold py-3 px-4 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-2 ${
                      currentPlan === 'pro' 
                        ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
                        : 'bg-[#10A37F] hover:bg-[#0D8C6D] text-white'
                    }`}
                  >
                    {currentPlan === 'pro' ? 'Paket Aktif' : isLoadingPro ? 'Memproses...' : 'Bayar dengan Xendit / QRIS'}
                  </button>
                </>
              )}
              {currentPlan !== 'pro' && !currentPlan && (
                <CouponInput
                  value={couponCodePro}
                  onChange={(e) => {
                    e.stopPropagation();
                    setCouponCodePro(e.target.value);
                    setCouponErrorPro('');
                  }}
                  error={couponErrorPro}
                />
              )}
              <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                Mau dipakai bareng? Plan <span className="font-semibold">Team Rp399.000</span>{' '}
                untuk 25.000 kredit dibagi sampai 4 orang, dibeli lewat menu{' '}
                <a href="/team" className="text-blue-600 dark:text-blue-400 hover:underline">Tim</a>.
              </p>
            </div>
          </div>
          )}

          {/* Refresh status */}
          <div className="bg-gray-50 dark:bg-[#202123] rounded-2xl shadow-lg p-8 mt-8">
            <div className="flex items-center mb-4">
              <p className="text-lg font-medium text-gray-900 dark:text-white">Refresh your status after you finish payment</p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`w-full sm:w-auto px-8 py-3 rounded-lg transition-colors text-center font-medium shadow-lg hover:shadow-xl ${
                isRefreshing 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              <span className="flex items-center justify-center">
                {isRefreshing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Checking Status...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh Status
                  </>
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Subscription;
